// packages/web/src/routes/route.ts — B1: the structured route model that
// sits on top of useHashRoute's raw `#/path` string. Five tabs (Today,
// Auditions, [Knot], Projects, Career), each with its own sub-view state
// (a stage/phase filter, a section, or the Materials workshop reached only
// from Today) — all encoded into the hash path itself so a route is a
// plain string (shareable/bookmarkable/back-button-able), not hidden
// component state that a hash change can't reconstruct.
//
// The four Strip consumers (Auditions/Projects/Career/Today's Materials —
// see components/Strip.tsx) each own one of the id lists below; every
// unrecognised or missing sub-segment falls back to that list's first
// entry (see `parseRoute`), so a bare `/auditions` and `/auditions/all`
// are the same route.

export interface StripOption<Id extends string> {
  id: Id;
  label: string;
}

export type AuditionStage =
  | 'all'
  | 'self-submitted'
  | 'audition'
  | 'callback'
  | 'chemistry-read'
  | 'screen-test'
  | 'pinned'
  | 'booked'
  | 'archived'
  // B21 — the household's own submissions funnel, folded into this same
  // route rather than kept as a separate screen (`#/auditions/submissions`).
  // Not a member of AUDITION_STAGES (the old pack's Strip vocabulary): this
  // id exists only so the URL can carry it, and HouseholdAuditions is the
  // one place that gives it a meaning.
  | 'submissions';

export const AUDITION_STAGES: StripOption<AuditionStage>[] = [
  { id: 'all', label: 'All' },
  { id: 'self-submitted', label: 'Self-submitted' },
  { id: 'audition', label: 'Audition' },
  { id: 'callback', label: 'Callback' },
  { id: 'chemistry-read', label: 'Chemistry Read' },
  { id: 'screen-test', label: 'Screen Test' },
  { id: 'pinned', label: 'Pinned' },
  { id: 'booked', label: 'Booked' },
  { id: 'archived', label: 'Archived' },
];

export type ProjectPhase = 'all' | 'auditioned' | 'booked' | 'released';

export const PROJECT_PHASES: StripOption<ProjectPhase>[] = [
  { id: 'all', label: 'All' },
  { id: 'auditioned', label: 'Auditioned' },
  { id: 'booked', label: 'Booked' },
  { id: 'released', label: 'Released' },
];

export type CareerSection = 'money' | 'documents' | 'development' | 'stats';

export const CAREER_SECTIONS: StripOption<CareerSection>[] = [
  { id: 'money', label: 'Money' },
  { id: 'documents', label: 'Documents' },
  { id: 'development', label: 'Development' },
  { id: 'stats', label: 'Stats' },
];

// A10 — `tapes` joins the union for the actor-assist pack, whose Materials
// tab is the tape log rather than the Actor pack's gallery. The UNION is
// shared because the route is shared; the two STRIPS below are not, because
// the two packs have different materials and a chip for a surface a pack
// does not have is a dead end.
export type MaterialsSection = 'me' | 'headshots' | 'resume' | 'reels' | 'tapes' | 'photos';

export const MATERIALS_SECTIONS: StripOption<MaterialsSection>[] = [
  { id: 'me', label: 'This is me' },
  { id: 'headshots', label: 'Headshots' },
  { id: 'resume', label: 'Resume' },
  { id: 'reels', label: 'Reels' },
];

/** The actor-assist pack's three. `tapes` is FIRST, so it is what findId
 * falls back to — a bare `#/materials` on this pack still lands where it
 * always did, which is the tape log. `photos` joined 2026-09-20: `aa_media`
 * has always declared `kind: 'photo'` and nothing on this strip ever showed
 * one (MaterialsPhotos.tsx's own header has the full account). */
export const AA_MATERIALS_SECTIONS: StripOption<MaterialsSection>[] = [
  { id: 'tapes', label: 'Tapes' },
  // 2026-09-24: the page publishes headshots only, and this pack had no door
  // that could make one. Same screen as Photos, `kind="headshot"`.
  { id: 'headshots', label: 'Headshots' },
  // B11: the page also publishes reels, and until this the pack had no door
  // that could make one either — `MaterialsReelsAssist.tsx`, named so it is
  // never confused with the retired Actor pack's own `MaterialsReels.tsx`.
  { id: 'reels', label: 'Reels' },
  { id: 'photos', label: 'Photos' },
  { id: 'resume', label: 'Resume' },
];

// S1: the TopTab union is PACK-AGNOSTIC — NavTabDef.tab is opaque to Core
// by design (a pack's declared tab string must match one of these routable
// keys, and Core can't see this union), so the web owns the superset.
// 'today'..'career' are Actor's four (their routes and default
// destinations are byte-identical to before S1); the rest are Rexx's
// (wf02 §01 admin/dispatch/owner rails + wf03's tech phone tabs). An
// Actor build never declares the Rexx keys, so nothing about its nav
// changes — see App.tsx's vertical gate for the hand-typed-hash case.
export type TopTab =
  | 'today'
  | 'auditions'
  | 'projects'
  | 'career'
  | 'clients'
  | 'work_orders'
  | 'money'
  | 'techs'
  | 'board'
  | 'schedule'
  | 'map'
  | 'numbers'
  | 'jobs'
  | 'chat'
  | 'pay'
  // A3: actor_assist's rail (the pack declares it in
  // verticals/actor-assist/src/index.ts — three groups, nine tabs). Four of
  // its keys already existed above and are reused unchanged: `today`,
  // `auditions`, `projects` and `money`. These five are the rest. A build on
  // any other pack never declares them, so nothing about Actor's or Rexx's
  // nav changes — the same property S1 established when it added Rexx's.
  //
  // `materials` was already a routable SCREEN (Actor reaches its workshop
  // from Today's strip) and is only now also a TAB. That is the whole
  // difference: the same screen, promoted to the rail by a pack that thinks
  // materials are daily work rather than a side trip.
  | 'people'
  | 'documents'
  | 'stats'
  | 'materials'
  | 'development'
  // B16a — the cockpit's two new tabs (the personal pack's surface; spec
  // `the-cockpit-surface-b16-spec-2026-08-24`). 'review' is deliberately
  // NOT 'asks' — that key is Rexx's and lives in REXX_SCREENS' gate.
  | 'threads'
  | 'review'
  // B17a — the calendar.
  | 'calendar'
  // B17b — the to-dos.
  | 'todos'
  // B17c — the people.
  // `people` already added by actor_assist above — one key, two packs' tabs.
  // B18d — the client buckets. NOT 'clients': that key is Rexx's registry
  // rail (see this union's own note), and a pack declaring it would route
  // into a screen gated out of every non-rexx build. The LABEL says
  // Clients; the key says what it is.
  | 'buckets'
  // R3b — the AI Radar (spec radar-r3b-spec.md, direction
  // radar-in-the-cockpit-direction-2026-09-27). After Clients on the
  // personal pack's rail, same PERSONAL_SCREENS gate as every cockpit tab.
  | 'radar'
  // C1 (2026-09-28) — the Record window. After Radar on the personal pack's
  // rail, same PERSONAL_SCREENS gate.
  | 'record'
  // Build 1 of the 01B nav (2026-10-08) — the cockpit's Personal tab, which
  // holds Calendar · To-dos · Lists · People · Notes as sub-tabs.
  | 'personal'
  // The Audience tab (decision `4804fc96`, spec `the-audience-build-spec-
  // 2026-10-08`). Marketing lives here; customers live in Conversations —
  // only a PROMOTED inbox item reaches Threads. Desktop rail: first-class,
  // after Personal. Phone: behind More, with the unanswered count riding
  // the More badge (the same pattern that moved Review into More at
  // `6c5d04a0`).
  | 'audience'
  // The Delivery tab (Chunk PRG-W, SPEC.md phase 1): every programme Atlas
  // is running, hand-fed from a plan. Desktop rail: first-class, after
  // Accounts (same posture Audience took after Personal). Phone: behind
  // More, same posture as Review/Accounts/Radar/Record — PRG-W's own §1,
  // no badge: a programme's waiting-for-yes count already rides Review's.
  | 'delivery'
  // The Systems tab (Chunk SYS-W, SPEC.md phase 2): who is working on
  // what within what limits — agents, meters, fleet, doors, the
  // compliance calendar, recovery evidence. Desktop rail: first-class,
  // after Delivery (SYS-W's own §1). Phone: behind More, same posture as
  // Delivery/Review/Accounts/Radar/Record.
  | 'systems'
  // 2026-09-02 — the mail surface. actor_assist's alone: it reads what
  // reached a HOUSEHOLD's address and what each message became, which is a
  // question the cockpit answers on its Threads tab instead.
  | 'mail'
  // B17 §2 — what the Knot kept. Beside `mail` for the same reason: a Knot
  // note is an `aa_source`, invisible everywhere else once approved.
  | 'notes';

// B20a used to add `submissions` here — the front door before the audition,
// as its own tab. ⚑ B21 (2026-09-27) REMOVED IT. Huan's ruling: submissions
// live ON the Auditions page now, not behind a tab of their own ("don't want
// to push projects off the rails"). The pack no longer declares a
// `submissions` tab, so this union carries no member for it — see
// HouseholdAuditions.tsx for where the funnel actually lives now, and the
// Route union below for `submissionDetail`, which is unaffected.

/** The Personal tab's five sub-tabs, in the order plate 01B draws them.
 * The id is the URL's word (`#/personal/<id>`), so a reload or back/forward
 * keeps the place. */
export type PersonalSub = 'calendar' | 'todos' | 'lists' | 'people' | 'notes' | 'accounts';

export const PERSONAL_SUBS: StripOption<PersonalSub>[] = [
  { id: 'calendar', label: 'Calendar' },
  { id: 'todos', label: 'To-dos' },
  { id: 'lists', label: 'Lists' },
  { id: 'people', label: 'People' },
  { id: 'notes', label: 'Notes' },
  // G1 — connected Google accounts. Personal.tsx shows this tab only when the
  // tenant has Google connections turned on.
  { id: 'accounts', label: 'Accounts' },
];

/** The Audience tab's three sub-tabs (spec `the-audience-build-spec-2026-10-08`,
 * decision `4804fc96`), in the order plate 02 draws them. `home` is first, so
 * it is what findId falls back to — a bare `#/audience` lands on the tiles. */
export type AudienceSub = 'home' | 'inbox' | 'week';

export const AUDIENCE_SUBS: StripOption<AudienceSub>[] = [
  { id: 'home', label: 'Home' },
  { id: 'inbox', label: 'Inbox' },
  { id: 'week', label: 'Week' },
];

/** S11: the asks screen's two lenses, in route vocabulary. Deliberately
 * shorter than the server's own `routed_to_me`/`filed_by_me` — those are the
 * API's words, these are the URL's. */
export type AskInboxView = 'routed' | 'filed';

/** H3a (COCKPIT-HOME-COLUMNS-SPEC.md §H3) — the only five project slugs
 * that exist this chunk (H4a adds the fifth, 'studio'), mirroring core's own `PROJECT_SLUGS`
 * (packages/core/src/cockpit/columns.ts) exactly. A local copy rather than
 * an import from lib/api.ts: this file is otherwise dependency-free (every
 * other Strip vocabulary above is self-contained too), and the two are
 * pinned equal by cockpitBoard.test.ts / route.test.ts rather than left to
 * drift silently. */
// Studio Assist (coordinator scope addition, 2026-10-16, Huan's yes): a
// fifth project, slug 'studio' — nothing deployed yet, but it is still a
// real project screen/builder like the other four.
export const PROJECT_SLUGS = ['rexx', 'stays', 'voicestrip', 'origincare', 'studio'] as const;
export type ProjectSlug = (typeof PROJECT_SLUGS)[number];
export function isProjectSlug(v: string): v is ProjectSlug {
  return (PROJECT_SLUGS as readonly string[]).includes(v);
}

/** H4a (COCKPIT-HOME-COLUMNS-SPEC.md §H4) — which project slugs carry a
 * builder pane at all: everything except 'origincare' (its own register
 * says it has no pane assigned — spec's "also noted, no question needed").
 * Mirrors core's own `BUILDER_PROJECTS` (packages/core/src/cockpit/
 * builderFeed.ts) exactly; App.tsx reads this to decide the cockpit pane's
 * `occupant` on a project screen. */
export const BUILDER_PROJECT_SLUGS = ['rexx', 'stays', 'voicestrip', 'studio'] as const;
export function isBuilderProjectSlug(v: string): v is (typeof BUILDER_PROJECT_SLUGS)[number] {
  return (BUILDER_PROJECT_SLUGS as readonly string[]).includes(v);
}

/** routeLabel's own words for each project — matches the title
 * `DEFAULT_PERSONAL_COLUMNS` gives each project column exactly
 * (core/src/cockpit/columns.ts), so "the way back" never says something
 * different from what the board itself calls the same project. */
const PROJECT_SCREEN_LABEL: Readonly<Record<ProjectSlug, string>> = {
  rexx: 'Rexx',
  stays: 'Studio Stays',
  voicestrip: 'VoiceStrip',
  origincare: 'Origin Care',
  studio: 'Studio Assist',
};

export type Route =
  | { screen: 'today' }
  // H1a (COCKPIT-HOME-COLUMNS-SPEC.md §H1) — the cockpit's front page at
  // every width; `/today` stays its own screen (Today's own receipt
  // groups). Personal-only: App.tsx redirects the personal pack's bare
  // default route here (the brief's own "#/ lands there"); every other
  // pack's default stays `/`, i.e. `today`.
  | { screen: 'home' }
  // H3a — one project's own screen. `/project/<slug>`. Only the five slugs
  // above are ever reachable: parseRoute sends any other slug to the board
  // (`home`), never a blank screen or an arbitrary URL (the brief's own
  // words) — see parseRoute's 'project' arm below.
  | { screen: 'projectScreen'; slug: ProjectSlug }
  | { screen: 'materials'; section: MaterialsSection }
  | { screen: 'auditions'; stage: AuditionStage }
  // B2: an audition's own detail screen — reached by tapping a row in the
  // Auditions list, NOT a Strip sub-view (no stage id represents "one
  // specific audition"), so it's its own Route case rather than folding
  // into the stage union above. Keeping it in the hash (`/auditions/
  // detail/:id`) rather than local component state is what makes it
  // share this file's own "a route is a plain string" property (shareable/
  // bookmarkable/back-button-able) with every other screen here.
  | { screen: 'auditionDetail'; id: string }
  | { screen: 'projects'; phase: ProjectPhase }
  // B3: a project's own phase-adaptive detail — reached by tapping a poster,
  // NOT a Strip sub-view, so (exactly like auditionDetail) it's its own
  // Route case rather than a phase id. `/projects/detail/:id`.
  | { screen: 'projectDetail'; id: string }
  // A4: a ROLE's own surface — the character, and the owner of the sides
  // revision trail (decisions fdf126f6 + 15155d2f). It sits UNDER projects
  // in the hash (`/projects/role/:id`) because that is where it sits in the
  // model: Project > Role > Audition, mirroring Client > Location > Work
  // Order. It is the one screen in this app that is drilled to from two
  // directions — down from a project, and UP from an audition's breadcrumb —
  // which is exactly why its identity lives in the URL rather than in
  // whichever screen happened to open it.
  | { screen: 'roleDetail'; id: string }
  // A5: the far side of the seam. An ENGAGEMENT is what a won part became,
  // and it sits under projects for the same reason a role does — that is
  // where it sits in the hierarchy (`Project > Role > engagement`). Its own
  // Route case, not a role sub-view: a booking is a thing you open, share and
  // come back to, and A4's whole finding was that a record reachable only
  // through the editor is a record with no front door.
  | { screen: 'engagementDetail'; id: string }
  // A5: one shoot day — the 5:40am read. Under the booking it belongs to in
  // MEANING, but flat in the hash (`/day/:id`), because the parent reaches it
  // from a notification or a bookmark on a shoot morning and must not have to
  // pass through two screens to see a pickup time.
  | { screen: 'shootDay'; id: string }
  // A6: one tape. Flat in the hash (`/tape/:id`) for the same reason a shoot
  // day is — a parent reaches it from a coach's message or a bookmark, and
  // should not pass through two screens to see which take went.
  | { screen: 'tape'; id: string }
  // B3a provenance: the untouched source-document viewer. Reached from a
  // `.srclink` chip on either the audition breakdown or a booked project's
  // day-of card, so it carries `from` — the path to return to — encoded in
  // the hash itself (mirrors the demo's srcReturn/backFromSrc, but kept in
  // the URL so a source view stays shareable/bookmarkable/back-button-able
  // like every other route here). `/source/:id[/from/<return-path>]`.
  | { screen: 'source'; id: string; from?: string }
  | { screen: 'career'; section: CareerSection }
  // Account: sign-out + change-password. Reached via a persistent shell
  // icon (App.tsx's recordsChrome), not from within any one tab's flow —
  // its own BackLink always returns to Today (same convention Materials'
  // onBack already uses: a cross-cutting screen with no natural parent tab
  // lands on Today, not wherever the icon happened to be tapped from).
  | { screen: 'account' }
  // A3 — actor_assist's four screens that no earlier pack had a head for.
  // Each is this one account's own — the subject-scope prefix that used to
  // narrow them into one performer's career is gone (2026-09-19 ruling: an
  // account is one career, so there is nothing left to narrow between).
  | { screen: 'people' }
  | { screen: 'documents' }
  | { screen: 'stats' }
  | { screen: 'development' }
  // C6b (wf05 §03): preferences + connections + the HR record, one screen
  // with a visible line. Cross-cutting like Account (no parent tab; back
  // lands on Today). On a build whose pack declares none of the record
  // machinery the pane simply renders what the server answers.
  // S1c (decision eb659c8d): `state` carries an OUTCOME string, same
  // pattern as `personal`/`accounts` below (`connected | partial |
  // declined`) — Settings now hosts Connected accounts, but the server's
  // OAuth redirect still lands on the OLD `/#/personal/accounts/<outcome>`
  // address (packages/server/src/plugins/googleConnections.ts's `SCREEN`
  // constant; a server change is out of this chunk's web-only scope), so
  // App.tsx's redirect carries that outcome across to here rather than
  // dropping the one-time banner.
  | { screen: 'settings'; state?: string }
  // S1 — the Rexx work-order spine. The list's optional `state` is a
  // lifecycle-state filter carried as an OPAQUE string (the manifest owns
  // that vocabulary — the screen validates against the fetched schema
  // rather than this file hardcoding a status list). `/work_orders[/:state]`.
  | { screen: 'workOrders'; state?: string }
  // A work order's own screen — `/work_orders/detail/:id`, same convention
  // as auditionDetail/projectDetail. In S1 it renders the honest stub; the
  // wf01 spread is S2 and lands on this same route.
  | { screen: 'workOrderDetail'; id: string }
  // S1 — every other Rexx rail destination, each an honest placeholder
  // until its own chunk builds it (clients/money/techs S4, board/schedule/
  // map S3, numbers = the owner view, jobs/chat/pay = the wf03 tech shell,
  // S5). Real Route cases, not a catch-all: authority is a union and the
  // rail never hides a held destination, so each must be a navigable,
  // bookmarkable place that says what lands there.
  | { screen: 'clients' }
  // S4 — the registry drilldowns (wf02 §03: the hierarchy is the
  // navigation). A client's own screen and a site's, each with the id in
  // the hash (same convention as auditionDetail/workOrderDetail). The site
  // route lives under the `clients` head — `/clients/site/:id` — because a
  // site has no existence apart from its client (the manifest's own
  // master_detail says so) and the breadcrumb's truth is that you reached
  // it by climbing down through Clients.
  | { screen: 'clientDetail'; id: string }
  | { screen: 'siteDetail'; id: string }
  | { screen: 'money' }
  // S11 — the asks inbox. NOT a rail destination: the blessed rails (wf02
  // §01b admin · dispatch · owner) carry no Asks tab, and this screen is
  // reached from Today's "Needs you" and from the receipt a filed ask
  // leaves behind. `view` is the lens (routed to me / filed by me) and the
  // optional `id` is the ask a doorway arrived ON, so "the line I tapped on
  // Today" and "the ask now open" are the same thing — in the hash, like
  // every other route here. `/asks`, `/asks/filed`, `/asks/routed/:id`.
  | { screen: 'asks'; view: AskInboxView; id?: string }
  | { screen: 'techs' }
  // S4 — a technician's own screen. `/techs/detail/:id`.
  | { screen: 'techDetail'; id: string }
  | { screen: 'board' }
  | { screen: 'schedule' }
  | { screen: 'map' }
  | { screen: 'numbers' }
  | { screen: 'jobs' }
  // S5a — ONE job, and the id is the WORK ORDER's. Not the visit's: the job
  // is what wf03's header shows (WO-4417), and the Knot's pack-declared
  // capture contexts are hvac_work_order / hvac_client / hvac_site — a route
  // carrying the work-order id is the one that lets a capture taken on the
  // job screen pre-associate with the job (App.tsx's
  // CAPTURE_SUBJECT_BY_SCREEN). `/jobs/detail/:id`, the same convention as
  // auditionDetail/workOrderDetail. The bare `jobs` head keeps its meaning:
  // the job you are on NOW, which the screen resolves from the day.
  | { screen: 'jobDetail'; id: string }
  | { screen: 'chat' }
  // S5b — ONE conversation, addressed. The `chat` head serves two different
  // people (App.tsx picks by nav role, the same way `today` picks between
  // RexxHome and TechToday): the office reads conversations in Rexx, and a
  // tech's chat is Telegram. A DM lane's identity is (person, LANE) — "Tim
  // R. — office" and "Tim R. — dispatch" are two conversations — so the lane
  // is IN the address, not a filter beside it. `/chat/job/:workOrderId`,
  // `/chat/office/:membershipId`, `/chat/dispatch/:membershipId`.
  | { screen: 'conversation'; lane: ChatLane; id: string }
  | { screen: 'pay' }
  // B16a — the cockpit routes. The threads list's optional `state` is the
  // SERVER's own vocabulary carried as an opaque string (workOrders' own
  // convention): the client holds no copy of the state list.
  // `/threads[/:state]`, `/threads/detail/:id`, `/review`.
  | { screen: 'threads'; state?: string }
  // `from` is the RETURN PATH (the source viewer's own convention): Mark
  // done and Back land wherever you were — Today, or the filter you had —
  // never a bare list that still shows the closed thread (Huan, 2026-08-25:
  // "it takes me to the all view so it looks like it didn't do anything").
  | { screen: 'threadDetail'; id: string; from?: string }
  | { screen: 'review' }
  // B17a — the agenda and one event (same from-convention as threadDetail).
  | { screen: 'calendar' }
  | { screen: 'calendarDetail'; id: string; from?: string }
  // B17b — the to-dos list; `state` is the filter (doing/open/done), the
  // Threads convention.
  | { screen: 'todos'; state?: string }
  // B17c — the people list and one person's spread (same from-convention
  // as threadDetail/calendarDetail).
  | { screen: 'people' }
  | { screen: 'peopleDetail'; id: string; from?: string }
  // B18d — what every client is telling us, by bucket.
  | { screen: 'buckets' }
  // K3 — the Agreement (c4-agreement.md). Personal-only for now; the
  // Account spread (K1, a sibling chunk) will link here once it lands.
  | { screen: 'agreements' }
  | { screen: 'agreementSpread'; id: string }
  // R3b — the AI Radar. `/radar` (home), `/radar/topic/<slug>`,
  // `/radar/proposal/<id>`, `/radar/topics` (the topics window). All
  // personal-only (SCREEN_OWNERS ['personal'] in the R3b spec's own words —
  // App.tsx's real mechanism is PERSONAL_SCREENS, the same set threads/
  // review/todos/buckets already ride).
  | { screen: 'radar' }
  // R3d (C3) — `runId` scopes the topic screen to one earlier day's own
  // items ("Earlier days" — a path segment, `/radar/topic/<slug>/run/<id>`,
  // not a `?run=` query string: this router splits the raw hash on '/' with
  // no query-string layer of its own (routes/useHashRoute.ts), so a literal
  // '?' would ride inside the slug segment and corrupt it).
  | { screen: 'radarTopic'; slug: string; runId?: string }
  | { screen: 'radarProposal'; id: string }
  | { screen: 'radarTopics' }
  // R3e (C) — the newsletter: the queue, Manage, Publish, past issues.
  // `/radar/newsletter`.
  | { screen: 'radarNewsletter' }
  // C1 (2026-09-28) — the Record window: what was generated on Huan's
  // behalf, by whom, and the door to read or download it. Personal-only,
  // same SCREEN_OWNERS/PERSONAL_SCREENS mechanism as the radar block above.
  // `/record` (home/Recent), `/record/page/<slug>`, `/record/file/<id>`,
  // `/record/browse/<axis>[/<value>]`, `/record/reading`.
  | { screen: 'record' }
  | { screen: 'recordPage'; slug: string }
  | { screen: 'recordFile'; id: string }
  | { screen: 'recordBrowse'; axis: string; value?: string }
  | { screen: 'recordReading' }
  // Build 1 of the 01B nav — the Personal tab. `sub` is the sub-tab;
  // `state` rides only the To-dos sub-tab (its filter, the Todos
  // convention) and `listId` only the Lists sub-tab (the list open), so a
  // reload lands on the same list. `/personal[/<sub>[/<state|listId>]]`.
  | { screen: 'personal'; sub: PersonalSub; state?: string; listId?: string }
  // The Audience tab (decision `4804fc96`). `sub` is the sub-tab; `weekStart`
  // rides only the Week sub-tab (an ISO date, this router's own run-id-style
  // extra path segment — there is no query-string layer here, see radarTopic's
  // own note). `/audience[/<sub>[/<weekStart>]]`.
  | { screen: 'audience'; sub: AudienceSub; weekStart?: string }
  // One inbox item, opened from the list. `/audience/inbox/:id`.
  | { screen: 'audienceInboxDetail'; id: string }
  // Chunk PRG-W — the Delivery list. `/delivery`.
  | { screen: 'delivery' }
  // One programme's own screen (plate: Programme). `/delivery/programmes/:id`.
  | { screen: 'programmeDetail'; id: string }
  // Chunk SYS-W — the Systems screen (plate: Systems). A row's history
  // opens in a side sheet, not a route — it is UI state on this one
  // screen, never deep-linked. `/systems`.
  | { screen: 'systems' }
  // K1 (console kernel, c2-account-kernel.md) — the Account, drilled into
  // from its row atop the Clients (buckets) screen. `/buckets/account/:id`,
  // not `/clients/...` — that prefix is Rexx's registry (this file's own
  // 'buckets' vs 'clients' note above); reusing it here would collide with
  // Rexx's rail-slot and routeLabel handling for no reason this screen
  // needs.
  | { screen: 'accountSpread'; id: string }
  // K2's consumer (c3-lifecycle-kernel.md) — one bounded engagement under
  // an Account. `/buckets/engagement/:id`, same reasoning as accountSpread.
  | { screen: 'engagementSpread'; id: string }
  // 2026-09-02 — the household's mail: what arrived, and what it became.
  | { screen: 'mail' }
  // B17 §2 — what the Knot kept (a More row beside Mail, not a Today line).
  | { screen: 'notes' }
  // B8 — the performer's public page (preview/address/publish/live), reached
  // from More → Your page and from the end of actor_assist first-run (B5's
  // `continueToPage` seam). Cross-cutting like Account/Settings — no rail
  // slot of its own, back lands on Today — because it is reached from a
  // sheet, not from the bottom bar.
  | { screen: 'yourPage' }
  // B20a — one submission's own spread, reached from HouseholdAuditions'
  // "Submissions" filter (or a row inside it). `/submissions/detail/:id`.
  //
  // ⚑ B21 (2026-09-27) — the LIST is no longer its own screen. `#/submissions`
  // used to parse to its own `submissions` screen case; it now parses to
  // the Auditions screen with the Submissions filter selected — see the
  // 'auditions'/'submissions' arms of parseRoute below. Old links and
  // receipts to `submissionDetail` are untouched.
  | { screen: 'submissionDetail'; id: string };

/** WHICH PACK A SCREEN BELONGS TO — the four vertical identities this fork
 * routes for. `actors` is the ORIGINAL July pack, and it is the floor: a
 * tenant that predates verticals carries no vertical string at all, and a
 * vertical nobody has written a gate for is safest read as the pack the app
 * shipped with. */
export type PackVertical = 'actor_assist' | 'rexx' | 'personal' | 'actors';

/** Normalises whatever the tenant/deployment carries into one of the four.
 * Anything unrecognised — null on a pre-vertical tenant, a vertical this
 * fork has no screens for — is the July pack. */
export function packVerticalOf(vertical: string | null | undefined): PackVertical {
  return vertical === 'actor_assist' || vertical === 'rexx' || vertical === 'personal'
    ? vertical
    : 'actors';
}

const EVERY_PACK: readonly PackVertical[] = ['actor_assist', 'rexx', 'personal', 'actors'];

/** ⚑ THE OWNER REGISTRY — EVERY SCREEN NAMES THE PACKS IT BELONGS TO.
 *
 * This replaces the pair of claim-sets plus a personal-only denylist that
 * App.tsx carried until 2026-09-01, and it is the refactor that file's own
 * comment asked for by name ("when a third vertical arrives, the honest
 * general fix is an owner registry per screen, not a third denylist"). The
 * third vertical had arrived, and four holes were open in the meantime:
 * `career` was claimed by nobody, so a CallToWrap or Rexx tenant hand-typing
 * `#/career` rendered the July pack's Career sections over a namespace with
 * no records in it; Rexx had no actor denylist at all, so `#/auditions`,
 * `#/projects` and `#/materials` rendered July screens on a trade tenant; and
 * `roleDetail`/`engagementDetail`/`shootDay`/`tape` sat in no set, so every
 * vertical could render an actor_assist surface by URL.
 *
 * A Record over `Route['screen']` rather than a set of sets, so a screen
 * added to the union above with no owner is a COMPILE error rather than a
 * silently-reachable one — the hole this table exists to close cannot be
 * reopened by forgetting.
 *
 * A head two packs share (`money` for Rexx and actor_assist, `people` and
 * `peopleDetail` for actor_assist and the cockpit, `auditions`/`projects`/
 * `materials` for actor_assist and the July pack) lists both: the route is
 * shared, the screen behind it is not, and App.tsx's switch is where that is
 * decided. Reachability and rendering stay two questions.
 */
export const SCREEN_OWNERS: Readonly<Record<Route['screen'], readonly PackVertical[]>> = {
  // Cross-cutting: every pack lands somewhere, and every pack has an account.
  today: EVERY_PACK,
  account: EVERY_PACK,
  settings: EVERY_PACK,
  // H1a — the cockpit's own front page. Personal-only, same posture as
  // every other cockpit-only tab in this registry.
  home: ['personal'],
  // H3a — one project's own screen. Personal-only, same posture as `home`.
  projectScreen: ['personal'],

  // The July pack and actor_assist share these heads; App.tsx branches on
  // the vertical INSIDE each case (aa_audition and actor_audition are
  // different namespaces with zero overlap).
  auditions: ['actor_assist', 'actors'],
  auditionDetail: ['actor_assist', 'actors'],
  projects: ['actor_assist', 'actors'],
  projectDetail: ['actor_assist', 'actors'],
  materials: ['actor_assist', 'actors'],

  // The untouched source document. Not an actor surface at all — Rexx's
  // work-order spread and job screen open it from their own `.srclink`
  // chips, which is why the trade pack is on this line and on no other in
  // this block. The cockpit reaches no source and never did.
  source: ['actor_assist', 'rexx', 'actors'],

  // The July pack alone. Its four sections ARE Money/Documents/Development/
  // Stats (CAREER_SECTIONS above) over `actor_*` types; the packs that came
  // after have their own heads for the same concerns.
  career: ['actors'],

  // actor_assist alone — the household surfaces. The four detail screens
  // read `aa_role`/`aa_engagement`/`aa_shoot_day`/`aa_tape` and have no
  // second rendering for any other pack.
  roleDetail: ['actor_assist'],
  engagementDetail: ['actor_assist'],
  shootDay: ['actor_assist'],
  tape: ['actor_assist'],
  documents: ['actor_assist'],
  stats: ['actor_assist'],
  development: ['actor_assist'],

  // Shared heads, one screen per pack.
  money: ['actor_assist', 'rexx'],
  people: ['actor_assist', 'personal'],
  peopleDetail: ['actor_assist', 'personal'],

  // The trade pack.
  workOrders: ['rexx'],
  workOrderDetail: ['rexx'],
  clients: ['rexx'],
  clientDetail: ['rexx'],
  siteDetail: ['rexx'],
  techs: ['rexx'],
  techDetail: ['rexx'],
  // ⚑ SHARED SINCE 2026-09-15 (the Day 1 walk, item 7) — the SAME hole G-19
  // found under `review` one line-block down, in a second place.
  //
  // The asks inbox is the ONE surface carrying the reasoned decline: it is
  // where a "Why not?" is typed and where `POST /api/asks/:id/decline` — a
  // route that REFUSES a reasonless decline — is called from. Every pack
  // raises asks (the authority gate converts rather than refuses, and
  // `actor_assist` routes its spine and capture actions at `ASK_THE_ADULTS`),
  // and `lib/attentionRoute` sends an `ask` attention tile straight here from
  // Today's "Needs you" on every pack alike.
  //
  // With this line naming Rexx alone, that tile's destination was rewritten
  // to Today by App.tsx's gate on a CallToWrap build: a parent could see that
  // somebody had asked, tap it, and arrive back where they started. Not
  // "reachable only by typing the route" — not reachable at all, and the
  // product's first-class reasoned decline with it.
  asks: ['rexx', 'actor_assist'],
  board: ['rexx'],
  schedule: ['rexx'],
  map: ['rexx'],
  numbers: ['rexx'],
  jobs: ['rexx'],
  jobDetail: ['rexx'],
  chat: ['rexx'],
  conversation: ['rexx'],
  pay: ['rexx'],

  // The cockpit.
  threads: ['personal'],
  threadDetail: ['personal'],
  // ⚑ SHARED SINCE 2026-09-11 (G-19), and it was a real hole rather than a
  // tidy-up. Every proposal a forwarded breakdown raises is staged in the
  // SAME queue this screen reads, on every pack — but this line named the
  // cockpit alone, so on a CallToWrap build `#/review` was rewritten to Today
  // by App.tsx's gate and the queue had no door ANYWHERE. Not "reachable only
  // by typing the route": not reachable at all. The household's Today now
  // carries the pending rows and links here for the full card, which is what
  // makes this ownership honest rather than merely wider.
  review: ['personal', 'actor_assist'],
  // A shared head, one screen per pack — the same shape as `money` and
  // `people` above. The cockpit's agenda is a list of events somebody sent
  // Huan; the household's is a list of records the family already owns, each
  // row a door into the audition, session or called day it belongs to. App
  // .tsx's switch decides which. `calendarDetail` stays the cockpit's alone:
  // it opens one calendar EVENT's preparation, and this pack has no event to
  // open.
  calendar: ['actor_assist', 'personal'],
  calendarDetail: ['personal'],
  // S1 (2026-10-15, decisions 4297868b/b23b0619) — now a SHARED head, same
  // shape as `calendar`/`money`/`people` above. actor_assist's `mail` is
  // still "what did this message become?" over its own aa_* store; the
  // cockpit's is the Gmail-backed Mail screen (accounts strip, message
  // list, reading pane) over GET /api/mail/*. App.tsx's switch branches by
  // vertical, same as it does for `calendar`.
  mail: ['actor_assist', 'personal'],
  // B17 §2 — actor_assist alone, same posture as `mail` above: what the
  // Knot kept is a question over this pack's own `aa_source` rows, not a
  // surface any other pack has a reason to render.
  notes: ['actor_assist'],
  todos: ['personal'],
  buckets: ['personal'],
  // GAP 57 merge — main's personal-only screens (it gated them by
  // PERSONAL_SCREENS in App.tsx; this registry replaced that set on ground).
  // K3 — the Agreement; K1/K2 — the Account and its engagements.
  agreements: ['personal'],
  agreementSpread: ['personal'],
  accountSpread: ['personal'],
  engagementSpread: ['personal'],
  // R3b — the AI Radar (the spec's own words: SCREEN_OWNERS ['personal']).
  radar: ['personal'],
  radarTopic: ['personal'],
  radarProposal: ['personal'],
  radarTopics: ['personal'],
  radarNewsletter: ['personal'],
  // C1 — the Record window.
  record: ['personal'],
  recordPage: ['personal'],
  recordFile: ['personal'],
  recordBrowse: ['personal'],
  recordReading: ['personal'],
  // Build 1 of the 01B nav — the cockpit's Personal tab.
  personal: ['personal'],
  // The Audience tab (decision `4804fc96`) — personal-only, same posture as
  // every other cockpit-only tab in this registry.
  audience: ['personal'],
  audienceInboxDetail: ['personal'],
  // Chunk PRG-W — personal-only, same posture as every other cockpit-only
  // tab in this registry.
  delivery: ['personal'],
  programmeDetail: ['personal'],
  // Chunk SYS-W — personal-only, same posture.
  systems: ['personal'],
  // B8 — actor_assist alone. The performer's public page is F5's own
  // household-side surface; no other pack has a subject to build one for.
  yourPage: ['actor_assist'],
  // B20a — actor_assist alone, same posture as `notes`/`mail`: `aa_submission`
  // is this pack's own type. ⚑ B21 — the LIST is folded into `auditions`
  // above (same owners); only the detail spread keeps its own screen.
  submissionDetail: ['actor_assist'],
};

/** BUILD 1 OF THE 01B NAV — on the cockpit, To-dos and People are Personal's
 * sub-tabs, not tabs of their own. Their old addresses (`#/todos[/state]`,
 * `#/people` — deep links, Today's cards, a bookmark) keep working and land
 * INSIDE the Personal tab, with its strip and the Personal tab lit, rather
 * than as orphan screens the bar no longer names. Applied by App.tsx on the
 * personal pack only: `people` is also an actor_assist head, and that
 * pack's rendering is untouched.
 *
 * ⚑ `calendar` LEFT THIS FOLD ON S1b (decision c7ee4a1b, 2026-10-15) — same
 * shape as `review`'s own departure (2026-09-11, ScreenReachability's own
 * history): Calendar is now a first-class switcher door
 * (cockpitSurfaces.ts) with its OWN cockpit screen (CockpitCalendar), and
 * folding `#/calendar` back into the Personal tab here would silently
 * re-route every door that points at it (the switcher, Today's cards, a
 * bookmark) to the screen S1b just retired as a top-level surface — the
 * exact "green over an unreachable door" shape the `review` precedent
 * warns against. The Personal tab's OWN embedded calendar sub-tab
 * (`{screen:'personal', sub:'calendar'}`, CalendarSpread.tsx) is a
 * different route shape entirely and is untouched by this fold either way. */
export function foldIntoPersonal(route: Route): Route {
  switch (route.screen) {
    case 'todos':
      return route.state ? { screen: 'personal', sub: 'todos', state: route.state } : { screen: 'personal', sub: 'todos' };
    case 'people':
      return { screen: 'personal', sub: 'people' };
    default:
      return route;
  }
}

/** Whether a booted pack may render a screen at all. Everything else —
 * a hand-typed hash for another pack's surface, a bookmark carried across a
 * workspace switch — falls to Today, which is the fallback `parseRoute`
 * already gives an unrecognised path. */
export function screenOwnedBy(screen: Route['screen'], vertical: PackVertical): boolean {
  return SCREEN_OWNERS[screen].includes(vertical);
}

/** ⚑ THE SAME OWNER REGISTRY, ONE LEVEL DOWN — for the cards INSIDE a
 * cross-cutting screen. `settings` is EVERY_PACK, so the screen gate above
 * cannot keep one pack's card off another pack's Settings; until GAP 57's
 * inventory (2026-09-29) both of these rendered on every build, so a
 * CallToWrap parent was offered a weekly-hours ceiling and a margin floor,
 * and an owner was offered a raw read-only database key.
 *
 *   planPolicy  — K5 plan authorization's capacity/margin policy. The plan
 *                 record it governs is the cockpit's (the personal pack
 *                 includes PLAN_RECORD_TYPE); Rexx kept the card it had on
 *                 main (see the GAP 57 readiness report — on Rexx it
 *                 currently governs no record of its own).
 *   agentAccess — the read-only agent key (2026-09-01, built for Rexx's
 *                 owner; the cockpit has it too). Its own header says
 *                 multi-tenant hardening is owed before CallToWrap enables
 *                 it, so a household deployment must not offer it.
 *
 * Neither is a pack-declared switch today: the cockpit declares no `entry`
 * at all, so a flag inside `entry.settings` could not reach it without
 * giving it a front-door voice it does not have. A card nobody lists here
 * renders nowhere — the safe side. */
// S1c (decision eb659c8d) adds `connectedAccounts` — Google connections,
// moved here from Personal › Accounts (the cockpit's old `/personal/
// accounts` sub-tab; that route now redirects, see App.tsx). Personal
// only: no other pack has ever offered Google connections.
export type SettingsCard = 'planPolicy' | 'agentAccess' | 'connectedAccounts';

export const SETTINGS_CARD_OWNERS: Readonly<Record<SettingsCard, readonly PackVertical[]>> = {
  planPolicy: ['personal', 'rexx'],
  agentAccess: ['personal', 'rexx'],
  connectedAccounts: ['personal'],
};

export function settingsCardOwnedBy(card: SettingsCard, vertical: PackVertical | null | undefined): boolean {
  return vertical != null && SETTINGS_CARD_OWNERS[card].includes(vertical);
}

/** The three lanes `GET /api/comms/conversations` can answer with. The
 * SERVER decides which of them exist for a caller (core's `laneVisibleTo`);
 * this union is only the address vocabulary, never a visibility rule — the
 * client holds no copy of that predicate. */
export type ChatLane = 'job' | 'office' | 'dispatch';

const CHAT_LANES: readonly string[] = ['job', 'office', 'dispatch'];

function asChatLane(value: string | undefined): ChatLane | null {
  return value !== undefined && CHAT_LANES.includes(value) ? (value as ChatLane) : null;
}

// options is always one of this file's own non-empty StripOption consts
// (AUDITION_STAGES etc) — the non-null assertion below just tells the
// compiler what that invariant already guarantees.
function findId<Id extends string>(options: StripOption<Id>[], value: string | undefined): Id {
  const match = value !== undefined ? options.find((o) => o.id === value) : undefined;
  return (match ?? options[0]!).id;
}

/** Parses a raw hash path (e.g. "/auditions/callback") into a Route. Any
 * unknown top segment (including "/") lands on Today — the same fallback
 * App.tsx's predecessor used for its 404-as-Today behaviour. */
export function parseRoute(path: string): Route {
  const segments = path.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  const [head, sub, third] = segments;

  switch (head) {
    case 'home':
      return { screen: 'home' };
    // H3a — only the five known slugs resolve; anything else is the
    // board, never a blank screen or an arbitrary URL (the brief's own
    // rule — a hand-typed or stale `/project/<deleted-project>` link must
    // land somewhere real, not nowhere).
    case 'project':
      return sub && isProjectSlug(sub) ? { screen: 'projectScreen', slug: sub } : { screen: 'home' };
    case 'auditions':
      if (sub === 'detail' && third) return { screen: 'auditionDetail', id: third };
      // B21 — `submissions` is not in AUDITION_STAGES (the old pack's own
      // vocabulary; adding it there would offer the chip on a build with no
      // aa_submission at all), so it needs its own arm rather than falling
      // through findId's fallback-to-first-option rule.
      if (sub === 'submissions') return { screen: 'auditions', stage: 'submissions' };
      return { screen: 'auditions', stage: findId(AUDITION_STAGES, sub) };
    case 'projects':
      if (sub === 'detail' && third) return { screen: 'projectDetail', id: third };
      // A4. `role`, not `detail`, so the two drill-ins under /projects stay
      // distinguishable by the path alone.
      if (sub === 'role' && third) return { screen: 'roleDetail', id: third };
      // A5. `booked`, not `detail` — three drill-ins live under /projects now
      // and each stays distinguishable by the path alone.
      if (sub === 'booked' && third) return { screen: 'engagementDetail', id: third };
      return { screen: 'projects', phase: findId(PROJECT_PHASES, sub) };
    // A5 — /day/:id, flat and shallow ON PURPOSE. A parent opens this from a
    // notification at 5:40am; making them pass through the project and the
    // booking first would be two taps in a dark kitchen.
    case 'day':
      if (sub) return { screen: 'shootDay', id: sub };
      return { screen: 'today' };
    case 'tape':
      if (sub) return { screen: 'tape', id: sub };
      return { screen: 'materials', section: findId(MATERIALS_SECTIONS, undefined) };
    case 'source': {
      // /source/:id[/from/<return-path...>] — the return path is everything
      // after the "from" marker, rejoined into a leading-slash path.
      if (!sub) return { screen: 'today' };
      const fromIndex = segments.indexOf('from');
      const from = fromIndex >= 0 ? `/${segments.slice(fromIndex + 1).join('/')}` : undefined;
      return from ? { screen: 'source', id: sub, from } : { screen: 'source', id: sub };
    }
    case 'career':
      return { screen: 'career', section: findId(CAREER_SECTIONS, sub) };
    case 'materials':
      // A10: resolved against BOTH packs' section lists. This layer is
      // pack-agnostic by design and cannot know which pack is booted, so it
      // recognises every section either one declares and lets App dispatch.
      // Resolving against one list only would have silently turned
      // `#/materials/tapes` into `me` — a URL that works becoming a URL that
      // lands somewhere else, which is the kind of bug nobody reports.
      return {
        screen: 'materials',
        section: findId([...MATERIALS_SECTIONS, ...AA_MATERIALS_SECTIONS], sub),
      };
    case 'account':
      return { screen: 'account' };
    case 'settings':
      return sub ? { screen: 'settings', state: sub } : { screen: 'settings' };
    case 'work_orders':
      if (sub === 'detail' && third) return { screen: 'workOrderDetail', id: third };
      return sub ? { screen: 'workOrders', state: sub } : { screen: 'workOrders' };
    case 'clients':
      if (sub === 'detail' && third) return { screen: 'clientDetail', id: third };
      if (sub === 'site' && third) return { screen: 'siteDetail', id: third };
      // K3 — /clients/agreements (the list) and /clients/agreement/:id (the
      // spread), per c4-agreement.md's web spec. Distinct sub-segments
      // (plural vs singular) so they stay distinguishable by the path alone,
      // the same convention /projects/detail vs /projects/role/booked uses.
      if (sub === 'agreements') return { screen: 'agreements' };
      if (sub === 'agreement' && third) return { screen: 'agreementSpread', id: third };
      return { screen: 'clients' };
    case 'money':
      return { screen: 'money' };
    // `people` is ONE key claimed by two packs (actor_assist's registry tab
    // and the cockpit's B17c people). ONE parse arm serves both — it lives
    // with the cockpit cases below because only that pack has a detail
    // sub-route; a bare /people parses identically for both.
    case 'documents':
      return { screen: 'documents' };
    case 'stats':
      return { screen: 'stats' };
    case 'development':
      return { screen: 'development' };
    case 'asks': {
      // `/asks` and `/asks/routed` are the same route (the same fallback
      // rule the Strip sub-views use); a third segment is the ask to open.
      const view: AskInboxView = sub === 'filed' ? 'filed' : 'routed';
      return third ? { screen: 'asks', view, id: third } : { screen: 'asks', view };
    }
    case 'techs':
      if (sub === 'detail' && third) return { screen: 'techDetail', id: third };
      return { screen: 'techs' };
    case 'board':
      return { screen: 'board' };
    case 'schedule':
      return { screen: 'schedule' };
    case 'map':
      return { screen: 'map' };
    case 'numbers':
      return { screen: 'numbers' };
    case 'jobs':
      if (sub === 'detail' && third) return { screen: 'jobDetail', id: third };
      return { screen: 'jobs' };
    case 'chat': {
      // S5b: `/chat/<lane>/<id>` is one conversation. An unrecognised lane
      // falls back to the bare head — the same "unknown sub-segment means
      // the list" rule every Strip consumer already follows, so a mistyped
      // hash lands on the conversations list rather than nowhere.
      const lane = asChatLane(sub);
      if (lane && third) return { screen: 'conversation', lane, id: third };
      return { screen: 'chat' };
    }
    case 'pay':
      return { screen: 'pay' };
    case 'threads':
      if (sub === 'detail' && third) {
        const fromIndex = segments.indexOf('from');
        const from = fromIndex >= 0 ? `/${segments.slice(fromIndex + 1).join('/')}` : undefined;
        return from ? { screen: 'threadDetail', id: third, from } : { screen: 'threadDetail', id: third };
      }
      return sub ? { screen: 'threads', state: sub } : { screen: 'threads' };
    case 'review':
      return { screen: 'review' };
    case 'calendar':
      if (sub === 'detail' && third) {
        const fromIndex = segments.indexOf('from');
        const from = fromIndex >= 0 ? `/${segments.slice(fromIndex + 1).join('/')}` : undefined;
        return from ? { screen: 'calendarDetail', id: third, from } : { screen: 'calendarDetail', id: third };
      }
      return { screen: 'calendar' };
    case 'todos':
      return sub ? { screen: 'todos', state: sub } : { screen: 'todos' };
    case 'buckets':
      if (sub === 'account' && third) return { screen: 'accountSpread', id: third };
      if (sub === 'engagement' && third) return { screen: 'engagementSpread', id: third };
      return { screen: 'buckets' };
    case 'mail':
      return { screen: 'mail' };
    case 'notes':
      return { screen: 'notes' };
    case 'submissions':
      // B21 — an old bookmark/receipt. `submissionDetail` is unaffected; the
      // bare list folds into the Auditions screen with Submissions selected.
      if (sub === 'detail' && third) return { screen: 'submissionDetail', id: third };
      return { screen: 'auditions', stage: 'submissions' };
    case 'your-page':
      return { screen: 'yourPage' };
    case 'people':
      if (sub === 'detail' && third) {
        const fromIndex = segments.indexOf('from');
        const from = fromIndex >= 0 ? `/${segments.slice(fromIndex + 1).join('/')}` : undefined;
        return from ? { screen: 'peopleDetail', id: third, from } : { screen: 'peopleDetail', id: third };
      }
      return { screen: 'people' };
    case 'radar':
      if (sub === 'topic' && third) {
        // R3d (C3) — a 4th/5th segment `run/<id>` scopes to one earlier day.
        const runIndex = segments.indexOf('run');
        const runId = runIndex >= 0 ? segments[runIndex + 1] : undefined;
        return runId ? { screen: 'radarTopic', slug: third, runId } : { screen: 'radarTopic', slug: third };
      }
      if (sub === 'proposal' && third) return { screen: 'radarProposal', id: third };
      if (sub === 'topics') return { screen: 'radarTopics' };
      if (sub === 'newsletter') return { screen: 'radarNewsletter' };
      return { screen: 'radar' };
    case 'record':
      if (sub === 'page' && third) return { screen: 'recordPage', slug: third };
      if (sub === 'file' && third) return { screen: 'recordFile', id: third };
      if (sub === 'browse' && third) {
        const value = segments[3];
        return value ? { screen: 'recordBrowse', axis: third, value } : { screen: 'recordBrowse', axis: third };
      }
      if (sub === 'reading') return { screen: 'recordReading' };
      return { screen: 'record' };
    case 'personal': {
      const which = findId(PERSONAL_SUBS, sub);
      if (which === 'todos' && third) return { screen: 'personal', sub: which, state: third };
      if (which === 'lists' && third) return { screen: 'personal', sub: which, listId: third };
      // The callback's outcome (connected | partial | declined) rides here.
      if (which === 'accounts' && third) return { screen: 'personal', sub: which, state: third };
      return { screen: 'personal', sub: which };
    }
    case 'audience': {
      if (sub === 'inbox' && third) return { screen: 'audienceInboxDetail', id: third };
      const which = findId(AUDIENCE_SUBS, sub);
      if (which === 'week' && third) return { screen: 'audience', sub: which, weekStart: third };
      return { screen: 'audience', sub: which };
    }
    case 'delivery':
      if (sub === 'programmes' && third) return { screen: 'programmeDetail', id: third };
      return { screen: 'delivery' };
    case 'systems':
      return { screen: 'systems' };
    default:
      return { screen: 'today' };
  }
}

/** The inverse of parseRoute — builds the hash path for a Route. Always
 * round-trips through parseRoute to the same Route (used by navigate()
 * callers instead of hand-building path strings). */
export function routePath(route: Route): string {
  switch (route.screen) {
    case 'today':
      return '/';
    case 'home':
      return '/home';
    case 'projectScreen':
      return `/project/${route.slug}`;
    case 'auditions':
      return route.stage === 'all' ? '/auditions' : `/auditions/${route.stage}`;
    case 'auditionDetail':
      return `/auditions/detail/${route.id}`;
    case 'projects':
      return route.phase === 'all' ? '/projects' : `/projects/${route.phase}`;
    case 'projectDetail':
      return `/projects/detail/${route.id}`;
    case 'roleDetail':
      return `/projects/role/${route.id}`;
    case 'engagementDetail':
      return `/projects/booked/${route.id}`;
    case 'shootDay':
      return `/day/${route.id}`;
    case 'tape':
      return `/tape/${route.id}`;
    case 'source':
      // route.from is already a leading-slash path (e.g. "/projects/detail/x")
      // — appended after the "from" marker, its own leading slash absorbed.
      return route.from ? `/source/${route.id}/from${route.from}` : `/source/${route.id}`;
    case 'career':
      return route.section === 'money' ? '/career' : `/career/${route.section}`;
    case 'materials':
      return route.section === 'me' ? '/materials' : `/materials/${route.section}`;
    case 'account':
      return '/account';
    case 'settings':
      return route.state ? `/settings/${route.state}` : '/settings';
    case 'workOrders':
      return route.state ? `/work_orders/${route.state}` : '/work_orders';
    case 'workOrderDetail':
      return `/work_orders/detail/${route.id}`;
    case 'clients':
      return '/clients';
    case 'clientDetail':
      return `/clients/detail/${route.id}`;
    case 'siteDetail':
      return `/clients/site/${route.id}`;
    case 'money':
      return '/money';
    case 'people':
      return '/people';
    case 'documents':
      return '/documents';
    case 'stats':
      return '/stats';
    case 'development':
      return '/development';
    case 'asks':
      // The bare `/asks` is the routed lens — the one an ask arrives on.
      if (route.id) return `/asks/${route.view}/${route.id}`;
      return route.view === 'routed' ? '/asks' : '/asks/filed';
    case 'techs':
      return '/techs';
    case 'techDetail':
      return `/techs/detail/${route.id}`;
    case 'board':
      return '/board';
    case 'schedule':
      return '/schedule';
    case 'map':
      return '/map';
    case 'numbers':
      return '/numbers';
    case 'jobs':
      return '/jobs';
    case 'jobDetail':
      return `/jobs/detail/${route.id}`;
    case 'chat':
      return '/chat';
    case 'conversation':
      return `/chat/${route.lane}/${route.id}`;
    case 'pay':
      return '/pay';
    case 'threads':
      return route.state ? `/threads/${route.state}` : '/threads';
    case 'threadDetail':
      return route.from ? `/threads/detail/${route.id}/from${route.from}` : `/threads/detail/${route.id}`;
    case 'review':
      return '/review';
    case 'calendar':
      return '/calendar';
    case 'calendarDetail':
      return route.from ? `/calendar/detail/${route.id}/from${route.from}` : `/calendar/detail/${route.id}`;
    case 'todos':
      return route.state ? `/todos/${route.state}` : '/todos';
    case 'buckets':
      return '/buckets';
    case 'accountSpread':
      return `/buckets/account/${route.id}`;
    case 'engagementSpread':
      return `/buckets/engagement/${route.id}`;
    case 'mail':
      return '/mail';
    case 'notes':
      return '/notes';
    case 'submissionDetail':
      return `/submissions/detail/${route.id}`;
    case 'yourPage':
      return '/your-page';
    case 'peopleDetail':
      return route.from ? `/people/detail/${route.id}/from${route.from}` : `/people/detail/${route.id}`;
    case 'agreements':
      return '/clients/agreements';
    case 'agreementSpread':
      return `/clients/agreement/${route.id}`;
    case 'radar':
      return '/radar';
    case 'radarTopic':
      return route.runId ? `/radar/topic/${route.slug}/run/${route.runId}` : `/radar/topic/${route.slug}`;
    case 'radarProposal':
      return `/radar/proposal/${route.id}`;
    case 'radarTopics':
      return '/radar/topics';
    case 'radarNewsletter':
      return '/radar/newsletter';
    case 'record':
      return '/record';
    case 'recordPage':
      return `/record/page/${route.slug}`;
    case 'recordFile':
      return `/record/file/${route.id}`;
    case 'recordBrowse':
      return route.value ? `/record/browse/${route.axis}/${route.value}` : `/record/browse/${route.axis}`;
    case 'recordReading':
      return '/record/reading';
    case 'personal':
      if (route.sub === 'todos' && route.state) return `/personal/todos/${route.state}`;
      if (route.sub === 'lists' && route.listId) return `/personal/lists/${route.listId}`;
      if (route.sub === 'accounts' && route.state) return `/personal/accounts/${route.state}`;
      return `/personal/${route.sub}`;
    case 'audience':
      if (route.sub === 'week' && route.weekStart) return `/audience/week/${route.weekStart}`;
      return route.sub === 'home' ? '/audience' : `/audience/${route.sub}`;
    case 'audienceInboxDetail':
      return `/audience/inbox/${route.id}`;
    case 'delivery':
      return '/delivery';
    case 'programmeDetail':
      return `/delivery/programmes/${route.id}`;
    case 'systems':
      return '/systems';
  }
}

/** Which of the 5 bottom-nav slots a Route lights up. The Materials
 * workshop is reached only from Today's own strip, so it keeps Today lit —
 * entering it never reads as "leaving" the Today tab. */
export function topTab(route: Route): TopTab {
  switch (route.screen) {
    // H1a — the board has no rail slot of its own (the personal pack's
    // phone bottom bar keeps Today/Threads/Personal unchanged this chunk);
    // 'today' is the harmless, truthful-enough default, same posture
    // account/settings/asks/agreements already take below.
    case 'home':
    // H3a — a project screen has no rail slot either; same posture as
    // `home` just above (the board itself).
    case 'projectScreen':
    case 'today':
    case 'materials':
      return 'today';
    case 'auditions':
    case 'auditionDetail':
      return 'auditions';
    case 'projects':
    case 'projectDetail':
    case 'roleDetail':
    case 'engagementDetail':
    case 'shootDay':
      return 'projects';
    case 'tape':
      return 'materials';
    case 'source':
      // The source viewer keeps its return screen's tab lit (mirrors the
      // demo's navHi reading srcReturn) — parse `from` to find it; a bare
      // source view (no return) is a projects-provenance read by default.
      return route.from ? topTab(parseRoute(route.from)) : 'projects';
    case 'career':
      return 'career';
    case 'account':
    case 'settings':
      // No natural parent tab (see the Route cases' own comments) — Today
      // is the harmless default, same as an unrecognised route falling
      // back to Today in parseRoute.
      return 'today';
    case 'workOrders':
    case 'workOrderDetail':
      return 'work_orders';
    case 'clients':
    // S4: the drilldowns keep their list's rail slot lit — entering a
    // client (or its site) never reads as "leaving" Clients.
    case 'clientDetail':
    case 'siteDetail':
      return 'clients';
    case 'money':
      return 'money';
    case 'people':
      return 'people';
    case 'documents':
      return 'documents';
    case 'stats':
      return 'stats';
    case 'development':
      return 'development';
    case 'asks':
      // S11: no rail slot of its own — the blessed rails have no Asks tab,
      // and inventing one is a nav change this chunk is not. Today stays
      // lit, the same answer account/settings give for a screen with no
      // parent tab, and the truthful one: you got here from Today.
      return 'today';
    case 'techs':
    case 'techDetail':
      return 'techs';
    case 'board':
      return 'board';
    case 'schedule':
      return 'schedule';
    case 'map':
      return 'map';
    case 'numbers':
      return 'numbers';
    case 'jobs':
    // S5a: a job keeps the Jobs tab lit — entering one never reads as
    // leaving the tab, the same rule clients/clientDetail already hold.
    case 'jobDetail':
      return 'jobs';
    case 'chat':
    // S5b: an open conversation keeps the Chat tab lit — the same rule
    // jobs/jobDetail and clients/clientDetail already hold. On an OFFICE
    // rail there is no chat slot to light (the blessed rails carry none and
    // adding one is a manifest change this chunk is not), so nothing lights
    // — which is the truth, not a bug: the office reaches conversations
    // from Today, the same door the asks inbox uses.
    case 'conversation':
      return 'chat';
    case 'pay':
      return 'pay';
    case 'threads':
      return 'threads';
    case 'threadDetail':
      // Keeps its ORIGIN's tab lit when it knows one (a thread opened from
      // Today reads as Today work); the Threads tab otherwise.
      return route.from ? topTab(parseRoute(route.from)) : 'threads';
    case 'review':
      return 'review';
    case 'calendar':
      return 'calendar';
    case 'calendarDetail':
      return route.from ? topTab(parseRoute(route.from)) : 'calendar';
    case 'todos':
      return 'todos';
    case 'buckets':
    case 'accountSpread':
    case 'engagementSpread':
      // K1/K2 drill-ins keep the Clients (buckets) rail slot lit — entering
      // an Account (or one of its engagements) never reads as "leaving"
      // Clients, same convention Rexx's own clientDetail/siteDetail use.
      return 'buckets';
    case 'mail':
      return 'mail';
    case 'notes':
      return 'notes';
    // B21 — folded into Auditions; the detail spread keeps that tab lit
    // rather than a phantom 'submissions' slot the bar no longer has.
    case 'submissionDetail':
      return 'auditions';
    case 'yourPage':
      // No rail slot of its own (see the Route case's own comment) — Today,
      // the same answer account/settings give for a cross-cutting screen.
      return 'today';
    case 'peopleDetail':
      return route.from ? topTab(parseRoute(route.from)) : 'people';
    // R3b — the drilled-in radar screens keep the Radar rail slot lit,
    // same convention as buckets/accountSpread/engagementSpread above.
    case 'radar':
    case 'radarTopic':
    case 'radarProposal':
    case 'radarTopics':
    case 'radarNewsletter':
      return 'radar';
    // C1 — the drilled-in Record screens keep the Record rail slot lit,
    // same convention as radar's own block above.
    case 'record':
    case 'recordPage':
    case 'recordFile':
    case 'recordBrowse':
    case 'recordReading':
      return 'record';
    // K3 — no rail slot of its own yet (the Account spread, K1, is where
    // the personal pack's "Clients" tab will eventually link this from).
    // Today stays lit, the same answer `asks` gives for a screen reached
    // without a home tab.
    case 'agreements':
    case 'agreementSpread':
      return 'today';
    case 'personal':
      return 'personal';
    case 'audience':
    case 'audienceInboxDetail':
      return 'audience';
    case 'delivery':
    case 'programmeDetail':
      return 'delivery';
    case 'systems':
      return 'systems';
  }
}

/* ------------------------------------------------------------------ *
 * CV5 §2 — THE FRAME OWNS A WAY BACK.
 *
 * The phone frame rendered no back affordance at all, and AppShell.test.tsx
 * pinned that as deliberate. The consequence was that every drilled-in screen
 * hand-rolled its own escape, and any screen that forgot — or any STATE that
 * hid its first row: an error card, a loading beat, an overlay — was a dead
 * end in a window with no browser chrome to escape through (Huan's walk:
 * "have to rethink that UI").
 *
 * So the way back is DERIVED FROM THE ROUTE, in one place, rather than
 * threaded as a prop through ten screens. `parentRoute` answers the only
 * question the frame needs: does this route have a parent, and which one.
 *
 * WHICH ROUTES HAVE ONE, and why the list is not "everything with an id":
 * a parent exists where the route was reached BY DRILLING IN. The tab
 * destinations are not children of anything — `/jobs/detail/:id` in
 * particular is the CANONICAL form of the Jobs TAB itself (CV3 made the bare
 * tab rewrite to it), so a back from it would climb to the route that
 * immediately resolves back to the same job.
 *
 * The office's three registry drilldowns (client / site / tech) and the asks
 * inbox are deliberately ABSENT: each already renders a real breadcrumb — a
 * labelled, always-present climb that names every destination on the way up —
 * which is a way back, not the absence of one. Adding a frame affordance over
 * a breadcrumb would create exactly the second escape this section exists to
 * remove.
 * ------------------------------------------------------------------ */

/** The route a drilled-in screen climbs to, or null when this route IS a
 * destination. Pure: no component state, no per-screen props. */
export function parentRoute(route: Route): Route | null {
  switch (route.screen) {
    case 'materials':
      return { screen: 'today' };
    case 'auditionDetail':
      return { screen: 'auditions', stage: 'all' };
    case 'projectDetail':
      return { screen: 'projects', phase: 'all' };
    // A4: a role climbs to the projects list, not to its own project — the
    // project's id is not in the route and this function is pure. The role
    // SCREEN draws the true breadcrumb (project > role) from the record it
    // has already loaded; this is only the shell's structural back.
    case 'roleDetail':
      return { screen: 'projects', phase: 'all' };
    // A5: same reasoning as the role — this function is pure and the
    // engagement's role id is not in the route. The SCREEN draws the true
    // breadcrumb (project > role > booked) from records it has loaded.
    case 'engagementDetail':
    case 'shootDay':
      return { screen: 'projects', phase: 'all' };
    // A6: a tape climbs to Materials, which is where its list lives.
    case 'tape':
      return { screen: 'materials', section: findId(MATERIALS_SECTIONS, undefined) };
    case 'source':
      // The one route that already carried its parent, since B3a: the chip
      // that opened it put the return path in the hash. Without one there is
      // nothing honest to climb to, so the screen's own BackLink (whose
      // onClick the shell still supplies) stays the escape.
      return route.from ? parseRoute(route.from) : null;
    case 'account':
    case 'settings':
    case 'yourPage':
      // No parent TAB (see the Route cases' own comments) — Today, the same
      // answer topTab gives for a cross-cutting screen.
      return { screen: 'today' };
    case 'conversation':
      return { screen: 'chat' };
    case 'workOrderDetail':
      return { screen: 'workOrders' };
    case 'threadDetail':
      // Climbs to where it CAME from; the On-me list otherwise (the tab's
      // own default — never the all view, which still shows closed threads).
      return route.from ? parseRoute(route.from) : { screen: 'threads', state: 'waiting_me' };
    case 'calendarDetail':
      return route.from ? parseRoute(route.from) : { screen: 'calendar' };
    case 'peopleDetail':
      return route.from ? parseRoute(route.from) : { screen: 'people' };
    // K3 — an agreement climbs to the agreements list, unconditionally
    // (there is no Account spread to return to yet in this worktree).
    case 'agreementSpread':
      return { screen: 'agreements' };
    // R3b — every drilled-in radar screen climbs to the Radar home.
    case 'radarTopic':
    case 'radarProposal':
    case 'radarTopics':
    case 'radarNewsletter':
      return { screen: 'radar' };
    // C1 — every drilled-in Record screen climbs to the Record home,
    // same convention as radar's own block above.
    case 'recordPage':
    case 'recordFile':
    case 'recordBrowse':
    case 'recordReading':
      return { screen: 'record' };
    // B21 — climbs to Auditions with Submissions selected, not to a screen
    // that no longer exists.
    case 'submissionDetail':
      return { screen: 'auditions', stage: 'submissions' };
    // One inbox item climbs to the Inbox sub-tab it was opened from.
    case 'audienceInboxDetail':
      return { screen: 'audience', sub: 'inbox' };
    // Chunk PRG-W — a programme climbs to the Delivery list.
    case 'programmeDetail':
      return { screen: 'delivery' };
    default:
      return null;
  }
}

/** What the way back SAYS — the destination's own name, never a bare
 * "Back": a control that names where it goes is the difference between an
 * escape and a guess. */
export function routeLabel(route: Route): string {
  switch (route.screen) {
    case 'today':
      return 'Today';
    case 'home':
      return 'Home';
    // H3a — the project's own name, same four titles DEFAULT_PERSONAL_
    // COLUMNS gives each column (core's own cockpit/columns.ts).
    case 'projectScreen':
      return PROJECT_SCREEN_LABEL[route.slug];
    case 'auditions':
      return 'Auditions';
    case 'projects':
      return 'Projects';
    case 'career':
      return 'Career';
    case 'materials':
      return 'Materials';
    case 'chat':
      return 'Chat';
    case 'workOrders':
      return 'Work orders';
    case 'clients':
      return 'Clients';
    case 'techs':
      return 'Techs';
    case 'jobs':
    case 'jobDetail':
      return 'Jobs';
    case 'money':
      return 'Money';
    case 'people':
      return 'People';
    case 'documents':
      return 'Documents';
    case 'stats':
      return 'Stats';
    case 'development':
      return 'Development';
    case 'board':
      return 'Board';
    case 'schedule':
      return 'Schedule';
    case 'map':
      return 'Map';
    case 'numbers':
      return 'Numbers';
    case 'pay':
      return 'Pay';
    case 'asks':
      return 'Asks';
    // Build 1 of the 01B nav: the tab says Conversations and Accounts now, so
    // the way back says the same words the tab does.
    case 'threads':
      return 'Conversations';
    case 'review':
      return 'Review';
    case 'personal':
      // The way back from an event or a person opened on a sub-tab names the
      // sub-tab ("Calendar", "People") — where you actually were.
      return PERSONAL_SUBS.find((x) => x.id === route.sub)?.label ?? 'Personal';
    case 'audience':
      return AUDIENCE_SUBS.find((x) => x.id === route.sub)?.label ?? 'Audience';
    case 'delivery':
      return 'Delivery';
    case 'programmeDetail':
      return 'Programme';
    case 'systems':
      return 'Systems';
    case 'calendar':
      return 'Calendar';
    case 'todos':
      return 'To-dos';
    case 'buckets':
      // S1c (decision eb659c8d): business "Accounts" renamed "Clients" —
      // the switcher's own door, and everywhere else this screen's name
      // shows (back-links included), so the word never drifts between
      // the two.
      return 'Clients';
    case 'radar':
      return 'Radar';
    case 'record':
      return 'Record';
    case 'agreements':
      return 'Agreements';
    case 'mail':
      return 'Mail';
    case 'notes':
      return 'Notes';
    case 'yourPage':
      return 'Your page';
    case 'account':
      return 'Account';
    case 'settings':
      return 'Settings';
    case 'auditionDetail':
    case 'projectDetail':
    case 'roleDetail':
    case 'engagementDetail':
    case 'shootDay':
    case 'tape':
    case 'workOrderDetail':
    case 'clientDetail':
    case 'siteDetail':
    case 'techDetail':
    case 'conversation':
    case 'threadDetail':
    case 'calendarDetail':
    case 'peopleDetail':
    case 'agreementSpread':
    case 'submissionDetail':
    case 'audienceInboxDetail':
    case 'source':
    case 'accountSpread':
    case 'engagementSpread':
    case 'radarTopic':
    case 'radarProposal':
    case 'radarTopics':
    case 'radarNewsletter':
    case 'recordPage':
    case 'recordFile':
    case 'recordBrowse':
    case 'recordReading':
      // A drilled-in screen is never a back DESTINATION in this app (nothing
      // drills two levels), so this is unreachable from parentRoute — but the
      // union must be total, and "back" is the honest word for a place whose
      // name we would be inventing.
      return 'back';
  }
}

/** The bare default route each bottom-nav tab lands on. */
export function defaultRouteFor(tab: TopTab): Route {
  switch (tab) {
    case 'today':
      return { screen: 'today' };
    case 'auditions':
      return { screen: 'auditions', stage: 'all' };
    case 'projects':
      return { screen: 'projects', phase: 'all' };
    case 'career':
      return { screen: 'career', section: 'money' };
    case 'work_orders':
      return { screen: 'workOrders' };
    case 'clients':
      return { screen: 'clients' };
    case 'money':
      return { screen: 'money' };
    case 'people':
      return { screen: 'people' };
    case 'documents':
      return { screen: 'documents' };
    case 'stats':
      return { screen: 'stats' };
    case 'development':
      return { screen: 'development' };
    // A3: the Materials tab lands on the workshop's first section — the
    // same route Actor's Today strip has always produced for it.
    case 'materials':
      return { screen: 'materials', section: 'me' };
    case 'techs':
      return { screen: 'techs' };
    case 'board':
      return { screen: 'board' };
    case 'schedule':
      return { screen: 'schedule' };
    case 'map':
      return { screen: 'map' };
    case 'numbers':
      return { screen: 'numbers' };
    case 'jobs':
      return { screen: 'jobs' };
    case 'chat':
      return { screen: 'chat' };
    case 'pay':
      return { screen: 'pay' };
    case 'threads':
      // On me — the working set. The all view (which includes closed) is a
      // chip away, never the landing.
      return { screen: 'threads', state: 'waiting_me' };
    case 'review':
      return { screen: 'review' };
    case 'calendar':
      return { screen: 'calendar' };
    case 'todos':
      return { screen: 'todos' };
    case 'buckets':
      return { screen: 'buckets' };
    case 'radar':
      return { screen: 'radar' };
    case 'record':
      return { screen: 'record' };
    case 'mail':
      return { screen: 'mail' };
    case 'notes':
      return { screen: 'notes' };
    case 'personal':
      return { screen: 'personal', sub: 'calendar' };
    case 'audience':
      return { screen: 'audience', sub: 'home' };
    case 'delivery':
      return { screen: 'delivery' };
    case 'systems':
      return { screen: 'systems' };
  }
}

// A3 — THE SUBJECT SCOPE, DELETED (Huan's ruling, 2026-09-19): "AN ACCOUNT
// IS ONE CAREER. THERE IS NEVER A SECOND PERFORMER IN IT." The `/p/:id`
// path prefix existed so a parent could drill from the household into one
// child's career and back out; with exactly one performer per account there
// is nothing left to drill into, so the prefix, `parseSubjectScope` and
// `withSubjectScope` are gone rather than narrowed. A route is now always
// the bare route parseRoute already returns — nothing wraps it.
